"use client";

import { useConvexAuth } from "convex/react";
import { ArrowLeft, ChevronRight, Home, Search } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useMemo, useState } from "react";
import { renderTextWithLatex } from "@/components/LaTeXRenderer";
import { Logo } from "@/components/landing/Logo";
import {
  TEXTBOOK_MODULES,
  moduleForTopicId,
  titleFromSlug,
} from "@/lib/textbookModules";
import { cn } from "@/lib/utils";

type SubTopic = {
  _id: string;
  topicId: string;
  description: string;
  slug: string | null;
};
type TopicGroup = [string, SubTopic[]];

export type TextbookSidebarProps = {
  groupedTopics?: Record<string, SubTopic[]>;
  className?: string;
};

// Same row recipe as the dashboard's AppSidebar so the two navs read as one.
const rowClass = (active: boolean) =>
  cn(
    "flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors duration-200",
    active
      ? "bg-sidebar-accent font-semibold text-sidebar-accent-foreground"
      : "font-medium text-sidebar-foreground/85 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground",
  );

function Section({
  header,
  children,
}: {
  header: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mt-6 flex flex-col first:mt-4">
      <div className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-sidebar-foreground/55">
        {header}
      </div>
      <div className="flex flex-col gap-0.5">{children}</div>
    </div>
  );
}

export default function TextbookSidebar({
  groupedTopics,
  className,
}: TextbookSidebarProps) {
  const { isAuthenticated } = useConvexAuth();
  const pathname = usePathname();
  const [searchTerm, setSearchTerm] = useState("");
  // Manual open/closed overrides only apply to the page they were made on; a
  // navigation resets them so the new page's group is always revealed.
  const [toggled, setToggled] = useState<{
    forSlug: string | null;
    open: Record<string, boolean>;
  }>({
    forSlug: null,
    open: {},
  });

  const isOverview = pathname === "/textbook";
  const activeSlug = pathname?.startsWith("/textbook/")
    ? pathname.slice("/textbook/".length)
    : null;
  const groups = useMemo(
    () => Object.entries(groupedTopics ?? {}) as TopicGroup[],
    [groupedTopics],
  );

  // Page slugs are module-prefixed ("maths-2/..."), as are hub URLs ("maths-2"),
  // so the first segment is the module of whatever is open.
  const modules = useMemo(() => {
    const counts = new Map<string, number>();
    for (const [, subs] of groups) {
      for (const s of subs) {
        if (!s.slug) continue;
        const m = moduleForTopicId(s.topicId);
        if (m) counts.set(m.slug, (counts.get(m.slug) ?? 0) + 1);
      }
    }
    return TEXTBOOK_MODULES.filter((m) => counts.has(m.slug)).map((m) => ({
      ...m,
      count: counts.get(m.slug)!,
    }));
  }, [groups]);
  const currentModule =
    modules.find((m) => m.slug === activeSlug?.split("/")[0]) ?? modules[0];

  const q = searchTerm.trim().toLowerCase();
  const searching = q !== "";

  const visibleGroups = useMemo(() => {
    if (searching) {
      return groups
        .map(
          ([name, subs]) =>
            [
              name,
              subs.filter(
                (s) =>
                  name.toLowerCase().includes(q) ||
                  s.topicId.toLowerCase().includes(q) ||
                  s.description.toLowerCase().includes(q) ||
                  (s.slug
                    ? titleFromSlug(s.slug).toLowerCase().includes(q)
                    : false),
              ),
            ] as TopicGroup,
        )
        .filter(([, subs]) => subs.length > 0);
    }
    return groups.filter(
      ([, subs]) =>
        subs[0] &&
        moduleForTopicId(subs[0].topicId)?.slug === currentModule?.slug,
    );
  }, [groups, searching, q, currentModule?.slug]);

  const overrides = toggled.forSlug === activeSlug ? toggled.open : {};
  const toggleGroup = (name: string, open: boolean) =>
    setToggled({ forSlug: activeSlug, open: { ...overrides, [name]: !open } });

  return (
    <div
      className={cn(
        "flex h-full w-[280px] flex-col bg-sidebar px-4 pb-6",
        className,
      )}
    >
      <div className="flex h-16 shrink-0 items-center">
        <Logo />
      </div>

      <div className="flex flex-col gap-0.5 pt-4">
        <Link href="/textbook" className={rowClass(isOverview)}>
          <Home size={18} className="shrink-0 text-sidebar-foreground/70" />
          Overview
        </Link>
        {isAuthenticated && (
          <Link href="/dashboard" className={rowClass(false)}>
            <ArrowLeft
              size={18}
              className="shrink-0 text-sidebar-foreground/70"
            />
            Back to dashboard
          </Link>
        )}
      </div>

      <div className="relative mt-4">
        <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-sidebar-foreground/60" />
        <input
          type="text"
          placeholder="Search topics"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="h-9 w-full rounded-lg bg-sidebar-accent pl-9 pr-3 text-sm text-sidebar-accent-foreground outline-none transition-shadow placeholder:text-sidebar-foreground/60 focus:ring-2 focus:ring-sidebar-ring/40"
        />
      </div>

      <div className="no-scrollbar flex min-h-0 flex-1 flex-col overflow-y-auto">
        {groupedTopics && !searching && modules.length > 0 && (
          <Section header="Modules">
            {modules.map((m) => (
              <Link
                key={m.slug}
                href={`/textbook/${m.slug}`}
                className={cn(
                  rowClass(m.slug === currentModule?.slug),
                  "justify-between",
                )}
              >
                <span className="truncate">{m.name}</span>
                <span className="rounded-full bg-sidebar-accent px-2 py-0.5 text-[10px] font-bold text-sidebar-foreground/60">
                  {m.count}
                </span>
              </Link>
            ))}
          </Section>
        )}

        {groupedTopics && (
          <Section
            header={searching ? "Results" : (currentModule?.name ?? "Topics")}
          >
            {visibleGroups.map(([name, subs]) => {
              const containsActive = subs.some((s) => s.slug === activeSlug);
              const open = searching || (overrides[name] ?? containsActive);
              return (
                <div key={name}>
                  <button
                    type="button"
                    onClick={() => toggleGroup(name, open)}
                    aria-expanded={open}
                    className={cn(rowClass(false), "w-full")}
                  >
                    <ChevronRight
                      size={16}
                      className={cn(
                        "shrink-0 text-sidebar-foreground/60 transition-transform duration-200",
                        open && "rotate-90",
                      )}
                    />
                    <span className="flex-1 truncate text-left">{name}</span>
                    <span className="text-xs tabular-nums text-sidebar-foreground/55">
                      {subs.length}
                    </span>
                  </button>
                  {open && (
                    <div className="my-0.5 ml-[1.1rem] flex flex-col gap-0.5 border-l border-sidebar-border pl-2">
                      {subs.map((sub) => {
                        const hasPage = !!sub.slug;
                        const active = hasPage && activeSlug === sub.slug;
                        return (
                          <Link
                            key={sub._id}
                            href={hasPage ? `/textbook/${sub.slug}` : "#"}
                            aria-disabled={!hasPage}
                            aria-current={active ? "page" : undefined}
                            className={cn(
                              rowClass(active),
                              "py-1.5",
                              !hasPage && "pointer-events-none opacity-50",
                            )}
                          >
                            <span
                              className={cn(
                                "w-11 shrink-0 text-xs tabular-nums",
                                active
                                  ? "text-sidebar-primary"
                                  : "text-sidebar-foreground/55",
                              )}
                            >
                              {sub.topicId}
                            </span>
                            <span className="truncate">
                              {hasPage
                                ? titleFromSlug(sub.slug!)
                                : renderTextWithLatex(
                                    sub.description.split("\n")[0],
                                  )}
                            </span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
            {visibleGroups.length === 0 && (
              <p className="px-3 py-2 text-sm text-sidebar-foreground/60">
                No topics found.
              </p>
            )}
          </Section>
        )}
      </div>
    </div>
  );
}
